import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Box, Card, Typography, MenuItem, Select, InputLabel, FormControl } from '@mui/material';
import { DataGrid, GridColDef, GridInitialState } from '@mui/x-data-grid';
import { useApi } from '../../api/ApiProvider';
import Api from '../../axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import Spinner from '../Spinner';
import { FunctionalityDTO } from 'axiosApi/models';
import SearchInput from 'components/SearchInput';
import DefaultActions from 'components/DataGrid/DefaultActions';
import { ENTITY_TYPE } from 'common/constants';

const TYPE_LABELS: Record<number, string> = { 1: 'API Endpoint', 2: 'UI Component', 3: 'Business Rule' };

const INITIAL_STATE: GridInitialState = {
  pagination: { paginationModel: { page: 0, pageSize: 20 } },
};

const FunctionalityList = () => {
  const api: Api = useApi();
  const navigate = useNavigate();
  const toast = useToastMessageQueue();
  const [rows, setRows] = useState<FunctionalityDTO[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [module, setModule] = useState('');
  const [modules, setModules] = useState<string[]>([]);

  const fetchFunctionalities = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.employeeApi.apiVversionFunctionalityAllGet(
        '1', page + 1, pageSize, module || undefined, searchTerm || undefined, {}
      );
      const data = res.data?.data;
      setRows(data?.queryResult || []);
      setTotalRecords(data?.totalRecords || 0);
    } catch (e) {
      handleAPIError(e, toast, {});
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchTerm, module]);

  useEffect(() => { fetchFunctionalities(); }, [fetchFunctionalities]);

  useEffect(() => {
    api.employeeApi.apiVversionFunctionalityAllGet('1', 1, 500, undefined, undefined, {})
      .then(res => {
        const mods = [...new Set((res.data?.data?.queryResult || []).map((f: FunctionalityDTO) => f.module).filter((m): m is string => Boolean(m)))].sort();
        setModules(mods);
      }).catch(() => {});
  }, []);

  const columns: GridColDef[] = [
    { field: 'identifier', headerName: 'Identifier', flex: 2 },
    { field: 'name', headerName: 'Name', flex: 1 },
    {
      field: 'type', headerName: 'Type', width: 140,
      renderCell: (p) => TYPE_LABELS[p.value as number] || p.value
    },
    { field: 'module', headerName: 'Module', width: 130 },
    { field: 'service', headerName: 'Service', width: 160 },
    {
      field: 'actions', headerName: '', width: 80, sortable: false,
      renderCell: (params) => (
        <DefaultActions
          row={params.row}
          entityType={ENTITY_TYPE.ANY}
          onEdit={() => navigate(`/functionality/detail/${params.row.id}`)}
          editScopes={[]}
        />
      )
    }
  ];

  return (
    <Card sx={{ m: 2 }}>
      <Box sx={{ p: 3 }}>
        <Typography color="primary" variant="h2" sx={{ mb: 2 }}>Functionalities</Typography>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" gap={2} alignItems="center">
            <SearchInput
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0); }}
              onClear={() => { setSearchTerm(''); setPage(0); }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Module</InputLabel>
              <Select label="Module" value={module} onChange={(e) => { setModule(e.target.value as string); setPage(0); }}>
                <MenuItem value="">All</MenuItem>
                {modules.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        </Box>
        <Box style={{ width: '100%', display: 'flex' }}>
          {loading && <Spinner />}
          <DataGrid
            rows={rows}
            columns={columns}
            rowCount={totalRecords}
            loading={loading}
            initialState={INITIAL_STATE}
            paginationMode="server"
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(m) => { setPage(m.page); setPageSize(m.pageSize); }}
            pageSizeOptions={[5, 10, 20, 50]}
            disableRowSelectionOnClick
          />
        </Box>
      </Box>
    </Card>
  );
};

export default FunctionalityList;
